import store from 'app-components/utils/redux-store';
import { Set as ImmutableSet } from 'immutable';
import { chartOfAccountsSelectors, chartOfAccountsTypes } from '../chart-of-accounts';
import { CustomerAndProjects, Filter, FilterProps, mkFilter } from './filtersTypes';
import { type FilterObject, mkFilterObject } from './filtersTypes';

import { customersSelectors } from 'app-components/flux/customers';
import { type Customer } from 'app-components/flux/customers/customersTypes';
import { projectsSelectors } from 'app-components/flux/projects';
import { type Project, mkProject } from 'app-components/flux/projects/projectsTypes';

// web filter returns only an id which is the id of a category or a string name that needs to be mapped to an id
// also a type needs to be added to it
const categoryIdAndType = (catID: string) => {
  switch (catID) {
    case chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED:
      return { id: '0', type: chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED };
    case 'BALANCE_ADJUSTMENT_0':
      return { id: '0', type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT };
    case 'BALANCE_ADJUSTMENT_1':
      return { id: '1', type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT };
    case 'BALANCE_ADJUSTMENT_2':
      return { id: '2', type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT };
    case 'BALANCE_ADJUSTMENT_3':
      return { id: '3', type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT };
    case '24':
      return { id: '24', type: chartOfAccountsTypes.CoaTypeEnum.ACCOUNT };
    default:
      return { id: catID, type: chartOfAccountsTypes.CoaTypeEnum.CATEGORY };
  }
};

const convertFilterCategoriesToQCSCategories = (filterCategories: ImmutableSet<string>) => {
  const result = [];

  // We want to filter out these container categories because these are meaningless in QCS and the reports/report-configuration
  // endpoint won't accept them.  However this means in the revers transform, we have to put these so the UI display correctly
  filterCategories &&
    result.push(
      ...filterCategories
        .map((cat) => categoryIdAndType(cat))
        .filter(
          (item) =>
            item.id !== 'USAGE-PERSONAL' &&
            item.id !== 'USAGE-BUSINESS' &&
            item.id !== 'TRANSFER' &&
            item.id !== 'OTHER',
        ),
    );

  return result;
};

const stringCheck = (x: unknown): x is string => typeof x === 'string';

const convertQCSCategoriesToFilterCategories = (selectedCoas: { id: string; type: string }[]) => {
  // make approirate adjustments to the selectedCoas (reverse transform)
  const selectedCoaIds = selectedCoas.map((it) => {
    if (it.id === '0' && it.type === 'UNCATEGORIZED') {
      return 'UNCATEGORIZED';
    }
    if (it.id === '0' && it.type === 'BALANCE_ADJUSTMENT') {
      return 'BALANCE_ADJUSTMENT_0';
    }
    if (it.id === '1' && it.type === 'BALANCE_ADJUSTMENT') {
      return 'BALANCE_ADJUSTMENT_1';
    }
    if (it.id === '2' && it.type === 'BALANCE_ADJUSTMENT') {
      return 'BALANCE_ADJUSTMENT_2';
    }
    if (it.id === '3' && it.type === 'BALANCE_ADJUSTMENT') {
      return 'BALANCE_ADJUSTMENT_3';
    }
    if (it.id === '24' && it.type === 'ACCOUNT') {
      return '24';
    }
    return it.id;
  });
  const result: string[] = selectedCoaIds;

  if (store) {
    // get all possible values for each top-level category
    const allCoas = chartOfAccountsSelectors.getChartOfAccountsTree(store?.getState());
    const allBusinessCoaIds = allCoas
      .find((value) => value.id === 'USAGE-BUSINESS')
      ?.children?.map((item) => item.id)
      .toJS()
      .filter(stringCheck);
    const allPersonalCoaIds = allCoas
      .find((value) => value.id === 'USAGE-PERSONAL')
      ?.children?.map((item) => item.id)
      .toJS()
      .filter(stringCheck);
    const allTransferCoaIds = allCoas
      .find((value) => value.id === 'TRANSFER')
      ?.children?.map((item) => item.id)
      .toJS()
      .filter(stringCheck);
    const allOtherCoaIds = ['UNCATEGORIZED', 'BALANCE_ADJUSTMENT_0', 'BALANCE_ADJUSTMENT_1', 'BALANCE_ADJUSTMENT_3'];

    // add the top-level category if all selected === all possible
    const allBusinessCoasSelected = allBusinessCoaIds?.every((item) => selectedCoaIds.includes(item));
    allBusinessCoasSelected && result.push('USAGE-BUSINESS');

    const allPersonalCoasSelected = allPersonalCoaIds?.every((item) => selectedCoaIds.includes(item));
    allPersonalCoasSelected && result.push('USAGE-PERSONAL');

    const allTransferCoasSelected = allTransferCoaIds?.every((item) => selectedCoaIds.includes(item));
    allTransferCoasSelected && result.push('TRANSFER');

    const allOtherCoasSelected = allOtherCoaIds?.every((item) => selectedCoaIds.includes(item));
    allOtherCoasSelected && result.push('OTHER');
  }

  return result;
};

const convertFilterCustAndProjToQCSCustAndProj = (
  customersAndProjectsSelected: ImmutableSet<CustomerAndProjects> | CustomerAndProjects[],
) => {
  const result: { customer: string; projects: string[] }[] = [];
  customersAndProjectsSelected?.map((item: CustomerAndProjects) => {
    result.push({
      customer: item.customer?.id || '',
      projects: item.projects?.map((project) => project?.id || ''),
    });
  });
  return result;
};

const convertQCSCustAndProjToFilterCustAndProj = (custAndProjs: { customer: string; projects: string[] }[]) => {
  const clientsAndProjects: string[] = [];
  const customersAndProjectsSelected: CustomerAndProjects[] = [];
  let length = 0;

  if (store) {
    const customersFromStore = customersSelectors.getCustomers(store?.getState());
    const projectsFromStore = projectsSelectors.getProjects(store?.getState());

    custAndProjs.map(({ customer, projects }) => {
      const custObj = customersFromStore.find((cust) => cust.id === customer) as Customer;

      const projObjs = projects.reduce((acc, projId) => {
        const match = projectsFromStore.find((proj) => proj.id === projId);
        if (match) {
          acc.push(match as Project);
        } else {
          acc.push(mkProject({ id: '0' }));
          length += 1; // Increment length for the '0' project
        }
        return acc;
      }, [] as Project[]);

      customersAndProjectsSelected.push({ customer: custObj, projects: projObjs });

      // create a comma-separated string that starts with the custObj id with `:0` appended followed by a list of all project ids
      const clientsAndProjs = [
        `${custObj?.id}`,
        ...projObjs.map((proj) => (proj.id !== '0' ? `${proj.id}` : `${custObj.id}:0`)),
      ].join(',');
      clientsAndProjects.push(...clientsAndProjs.split(','));
    });
  }

  return {
    clientsAndProjects: [...new Set(clientsAndProjects)],
    customersAndProjectsSelected,
    length,
  };
};

export const convertFilterObjectToQCSFilter = (filter: FilterObject): Filter => {
  if (filter?.businesses?.size === 0 && filter?.numFilterItems === 0)
    return mkFilter({ filterItems: [], type: 'REPORT' });

  const qcsFilter: FilterProps = { filterItems: [], type: 'REPORT' };
  if (filter?.categories?.size) {
    const itemArr = convertFilterCategoriesToQCSCategories(filter.categories);
    qcsFilter?.filterItems?.push({ chartOfAccounts: itemArr, type: 'CHARTOFACCOUNT' });
  }
  if (filter?.accounts?.size) {
    const itemArr = filter.accounts.toArray().map((acc) => ({ id: acc }));
    qcsFilter?.filterItems?.push({ accounts: itemArr, type: 'ACCOUNT' });
  }
  if (filter?.tags?.size) {
    const itemArr = filter.tags.toArray().map((tag) => ({ id: tag }));
    qcsFilter?.filterItems?.push({ tags: itemArr, type: 'TAG' });
  }
  if (filter?.payeeNames?.size) {
    const itemArr = filter.payeeNames.toArray().map((payee) => payee);
    qcsFilter?.filterItems?.push({ payees: itemArr, type: 'PAYEE' });
  }
  if (filter?.businesses?.size) {
    const itemArr = filter.businesses.toArray().map((business) => ({
      type: business === 'PERSONAL' ? 'PERSONAL' : 'BUSINESS',
      id: business === 'PERSONAL' ? '0' : business,
    }));
    qcsFilter?.filterItems?.push({ usages: itemArr, type: 'USAGE' });
  }
  if (filter?.customersAndProjectsSelected?.size) {
    const itemArr = convertFilterCustAndProjToQCSCustAndProj(filter.customersAndProjectsSelected);
    qcsFilter?.filterItems?.push({ customerProjects: itemArr, type: 'CUSTOMERPROJECT' });
  }

  qcsFilter.id = filter.id;
  const returnFitler = mkFilter(qcsFilter);

  return returnFitler;
};

export const convertQCSFilterToFilterObject = (filter?: Filter) => {
  const retFilterObj = mkFilterObject({ id: filter?.id });
  let numItems = 0;

  if (!filter) {
    return retFilterObj;
  }

  return retFilterObj.withMutations((mutable) => {
    filter?.filterItems?.forEach((item) => {
      switch (item.type) {
        case 'USAGE':
          if (item.usages) {
            mutable.set(
              'businesses',
              ImmutableSet(item.usages.map((it) => (it.type === 'PERSONAL' ? 'PERSONAL' : it.id))),
            );
          }
          break;
        case 'ACCOUNT':
          if (item.accounts) {
            mutable.set('accounts', ImmutableSet(item.accounts.map((it) => it.id)));
            numItems += item.accounts.length;
          }
          break;
        case 'CHARTOFACCOUNT':
          if (item.chartOfAccounts) {
            const filterCategories = convertQCSCategoriesToFilterCategories(item.chartOfAccounts);
            mutable.set('categories', ImmutableSet(filterCategories));
            numItems += item.chartOfAccounts.length;
          }
          break;
        case 'TAG':
          if (item.tags) {
            mutable.set('tags', ImmutableSet(item.tags.map((it) => it.id)));
            numItems += item.tags.length;
          }
          break;
        case 'PAYEE':
          if (item.payees) {
            mutable.set('payeeNames', ImmutableSet(item.payees));
            numItems += item.payees.length;
          }
          break;
        case 'CUSTOMERPROJECT':
          if (item.customerProjects) {
            const { clientsAndProjects, customersAndProjectsSelected, length } =
              convertQCSCustAndProjToFilterCustAndProj(item.customerProjects);
            mutable.set('clientsAndProjects', ImmutableSet(clientsAndProjects));
            mutable.set('customersAndProjectsSelected', ImmutableSet(customersAndProjectsSelected));
            numItems += length;
          }
          break;
        default:
          break;
      }
    });
    mutable.set('numFilterItems', numItems);
  });
};
